/**
 * HR API Client
 * Epic 111: HR + Payroll Internalizáció
 *
 * Endpoint base: /api/v1/hr/
 */

import { api } from '@/api/client';

// ============================================
// TYPES
// ============================================

export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'SEASONAL';
export type SalaryType = 'MONTHLY' | 'HOURLY';
export type ShiftType = 'NORMAL' | 'AFTERNOON' | 'NIGHT';
export type AttendanceSource = 'PIN' | 'MANUAL' | 'IMPORT';
export type LeaveTypeCode =
  | 'ANNUAL'
  | 'SICK_SELF'
  | 'SICK_CHILD'
  | 'UNPAID'
  | 'MATERNITY'
  | 'PATERNITY'
  | 'BEREAVEMENT'
  | 'BLOOD_DONATION';
export type LeaveRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface Employee {
  id: string;
  tenantId: string;
  userId: string;
  employmentType: EmploymentType;
  contractStart: string;
  contractEnd?: string | undefined;
  probationEnd?: string | undefined;
  weeklyHours: number;
  grossSalary: number;
  salaryType: SalaryType;
  bankAccountNumber?: string | undefined;
  taxId?: string | undefined;
  tajNumber?: string | undefined;
  birthDate?: string | undefined;
  childrenCount: number;
  disabledChildrenCount: number;
  hasFamilyTaxCredit: boolean;
  familyCreditChildCount: number;
  isFirstMarriage: boolean;
  firstMarriageStart?: string | undefined;
  szepHotellery: number;
  szepCatering: number;
  szepLeisure: number;
  isActive: boolean;
  isDeleted: boolean;
  deletedAt?: string | undefined;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
  // Joined from User (enriched by backend)
  userName?: string | undefined;
  userEmail?: string | undefined;
}

export interface EmployeeListResponse {
  data: Employee[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
  };
}

export interface EmployeeResponse {
  data: Employee;
}

export interface CreateEmployeeInput {
  userId: string;
  employmentType: EmploymentType;
  contractStart: string;
  contractEnd?: string | undefined;
  weeklyHours: number;
  grossSalary: number;
  salaryType: SalaryType;
  bankAccountNumber?: string | undefined;
  taxId?: string | undefined;
  tajNumber?: string | undefined;
  birthDate?: string | undefined;
  childrenCount?: number | undefined;
  hasFamilyTaxCredit?: boolean | undefined;
  familyCreditChildCount?: number | undefined;
}

export interface UpdateEmployeeInput extends Partial<CreateEmployeeInput> {}

// Attendance

export interface AttendanceRecord {
  id: string;
  tenantId: string;
  employeeProfileId: string;
  date: string;
  shiftType: ShiftType;
  source: AttendanceSource;
  scheduledStart?: string | undefined;
  scheduledEnd?: string | undefined;
  actualStart?: string | undefined;
  actualEnd?: string | undefined;
  breakMinutes: number;
  overtimeMinutes: number;
  isHoliday: boolean;
  isWeekend: boolean;
  note?: string | undefined;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 106-2c HOTFIX: backend `AttendanceService.getMonthlyReport` `{ records, summary }` objektumot
 * ad vissza, NEM tiszta array-t. A hook a `records`-ot kell kiolvassa.
 */
export interface MonthlyAttendanceReport {
  records: AttendanceRecord[];
  summary: {
    totalWorkedDays: number;
    totalOvertimeMinutes: number;
    totalSickDays: number;
    totalLeaveDays: number;
  };
}

export interface MonthlyAttendanceResponse {
  data: MonthlyAttendanceReport;
}

export interface ClockInInput {
  pin?: string | undefined;
  shiftType?: ShiftType | undefined;
}

export interface ClockResponse {
  data: AttendanceRecord;
}

// Leave

export interface LeaveType {
  id: string;
  tenantId: string;
  name: string;
  code: LeaveTypeCode;
  defaultDaysPerYear: number;
  requiresApproval: boolean;
  requiresMedicalCert: boolean;
  paidPercentage: number;
}

export interface LeaveBalance {
  id: string;
  employeeProfileId: string;
  leaveTypeId: string;
  year: number;
  entitled: number;
  used: number;
  remaining: number;
  carriedOver: number;
  createdAt: string;
  updatedAt: string;
}

export interface LeaveRequest {
  id: string;
  tenantId: string;
  employeeProfileId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  workingDays: number;
  status: LeaveRequestStatus;
  approvedBy?: string | undefined;
  approvedAt?: string | undefined;
  reason?: string | undefined;
  medicalCertRef?: string | undefined;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RequestLeaveInput {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason?: string | undefined;
}

export interface LeaveRequestsResponse {
  data: LeaveRequest[];
}

export interface LeaveRequestResponse {
  data: LeaveRequest;
}

export interface LeaveBalanceResponse {
  data: LeaveBalance[];
}

export interface LeaveCalendarResponse {
  data: LeaveRequest[];
}

// Shift

export interface ShiftPattern {
  id: string;
  tenantId: string;
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  isNightShift: boolean;
}

export interface ShiftListResponse {
  data: ShiftPattern[];
}

export interface ShiftResponse {
  data: ShiftPattern;
}

export interface CreateShiftInput {
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  isNightShift: boolean;
}

export interface UpdateShiftInput extends Partial<CreateShiftInput> {}

// ============================================
// API FUNCTIONS — Employees
// ============================================

export async function listEmployees(): Promise<EmployeeListResponse> {
  return api.get<EmployeeListResponse>('/hr/employees');
}

export async function getEmployee(id: string): Promise<EmployeeResponse> {
  return api.get<EmployeeResponse>(`/hr/employees/${id}`);
}

export async function createEmployee(data: CreateEmployeeInput): Promise<EmployeeResponse> {
  return api.post<EmployeeResponse>('/hr/employees', data);
}

export async function updateEmployee(
  id: string,
  data: UpdateEmployeeInput
): Promise<EmployeeResponse> {
  return api.patch<EmployeeResponse>(`/hr/employees/${id}`, data);
}

// ============================================
// API FUNCTIONS — Attendance
// ============================================

export async function clockIn(data?: ClockInInput): Promise<ClockResponse> {
  return api.post<ClockResponse>('/hr/attendance/clock-in', data ?? {});
}

export async function clockOut(): Promise<ClockResponse> {
  return api.post<ClockResponse>('/hr/attendance/clock-out', {});
}

/** 106-2b HOTFIX: path-param URL aligned with backend (was query-param → 404). */
export async function getMonthlyAttendance(
  year: number,
  month: number
): Promise<MonthlyAttendanceResponse> {
  return api.get<MonthlyAttendanceResponse>(`/hr/attendance/monthly/${year}/${month}`);
}

// ============================================
// API FUNCTIONS — Leave
// ============================================

export async function requestLeave(data: RequestLeaveInput): Promise<LeaveRequestResponse> {
  return api.post<LeaveRequestResponse>('/hr/leave/request', data);
}

export async function approveLeave(id: string): Promise<LeaveRequestResponse> {
  return api.post<LeaveRequestResponse>(`/hr/leave/${id}/approve`, {});
}

export async function rejectLeave(id: string): Promise<LeaveRequestResponse> {
  return api.post<LeaveRequestResponse>(`/hr/leave/${id}/reject`, {});
}

/** 106-2: tenant leave type catalog — RequestLeaveForm Select + quota-strip filter */
export async function getLeaveTypes(): Promise<{ data: LeaveType[] }> {
  return api.get<{ data: LeaveType[] }>('/hr/leave/types');
}

/** 106-2: path-param URL aligned with backend controller */
export async function getLeaveBalance(
  employeeId: string,
  year: number
): Promise<LeaveBalanceResponse> {
  return api.get<LeaveBalanceResponse>(`/hr/leave/balance/${employeeId}/${year}`);
}

/** 106-2: self-balance shortcut — OPERATOR nem látja a saját employeeProfileId-jét közvetlen. */
export async function getMyLeaveBalance(
  year: number
): Promise<LeaveBalanceResponse & { employeeProfileId: string }> {
  return api.get<LeaveBalanceResponse & { employeeProfileId: string }>(
    `/hr/leave/my-balance/${year}`
  );
}

/** 106-2: path-param URL aligned with backend controller */
export async function getLeaveCalendar(
  year: number,
  month: number
): Promise<LeaveCalendarResponse> {
  return api.get<LeaveCalendarResponse>(`/hr/leave/calendar/${year}/${month}`);
}

export async function getMyLeaveRequests(): Promise<LeaveRequestsResponse> {
  return api.get<LeaveRequestsResponse>('/hr/leave/my-requests');
}

// ============================================
// API FUNCTIONS — Shifts
// ============================================

export async function listShifts(): Promise<ShiftListResponse> {
  return api.get<ShiftListResponse>('/hr/shifts');
}

export async function createShift(data: CreateShiftInput): Promise<ShiftResponse> {
  return api.post<ShiftResponse>('/hr/shifts', data);
}

export async function updateShift(id: string, data: UpdateShiftInput): Promise<ShiftResponse> {
  return api.patch<ShiftResponse>(`/hr/shifts/${id}`, data);
}

export async function deleteShift(id: string): Promise<void> {
  return api.delete<void>(`/hr/shifts/${id}`);
}
